import { useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { PageContainer } from '@/components/ui/page-container';
import { PageHeader } from '@/components/ui/page-header';
import { WorkflowWidget } from '@/features/workflows';
import { ConnectAssistantCard } from '@/components/account/ConnectAssistantCard';

/**
 * Home — landing dashboard. Migrated from Bulma to PageHeader + grid.
 *
 * Scope of this migration is intentionally narrow: keep the same two
 * surfaces (greeting + onboarding workflows) but on the new stack so
 * the AppShell context is meaningful. KPI cards, top-incidents
 * preview, upcoming-changes panel (per home.html mockup) are deferred
 * — they require new data hooks and aren't part of the current page.
 *
 * Greeting uses the time-of-day to feel less robotic on a page users
 * open at 2am during an incident. Falls back to a friendly default
 * when the time can't be determined (jsdom in tests).
 */

function greetingFor(date: Date): string {
  const hour = date.getHours();
  if (hour < 5) return 'Working late';
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export default function Home() {
  const { user } = useAuth();

  // Memoize so we don't recompute on every render; the greeting only
  // updates if the component remounts (page navigation), which is
  // fine for our purposes.
  const greeting = useMemo(() => greetingFor(new Date()), []);

  const headerTitle = user?.firstName ? `${greeting}, ${user.firstName}` : greeting;
  const headerSubtitle = user?.organization?.name;

  return (
    <PageContainer>
      <PageHeader title={headerTitle} subtitle={headerSubtitle} />

      {/* Onboarding & user-setup workflows side-by-side at lg+, stacked
          below. The widgets themselves are still Bulma-styled today;
          they'll be reskinned in a follow-up Phase 3 step. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <WorkflowWidget workflowId="onboarding" />
        <WorkflowWidget workflowId="user-setup" />
      </div>

      {/* Below the onboarding widgets: getting the gateway up comes first, and this
          hides itself once an assistant is connected. */}
      <div className="mt-4 lg:mt-6">
        <ConnectAssistantCard />
      </div>
    </PageContainer>
  );
}
